/**
 * 텍스트형 PPT 덱 참조(outline.free_design) 도우미.
 * 덱 본문은 서버(Storage)에 있고, 개요에는 다시 열 때 필요한 참조만 둔다.
 *   { deck_id, revision, title, preview_urls, outline_sig, slide_sigs, edited_ids, created_at }
 * outline.flow === 'deck_first' 이면 개요 카드 없이 바로 덱을 만든다(2026-09 이후 새 대화).
 */

export const DECK_FIRST = 'deck_first';

export const isDeckFirst = (outline) => outline?.flow === DECK_FIRST;

const CONTENT_KEYS_SKIP = new Set(['canvas', 'canvas_edited', 'free_design', 'image_url', 'preview_url', 'slide_id']);

const stableSlide = (slide) => {
  if (!slide || typeof slide !== 'object') return slide;
  const out = {};
  Object.keys(slide).sort().forEach((key) => {
    if (!CONTENT_KEYS_SKIP.has(key)) out[key] = slide[key];
  });
  return out;
};

const hash = (text) => {
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) h = ((h * 33) ^ text.charCodeAt(i)) >>> 0;
  return h.toString(36);
};

/** 슬라이드 글 내용이 바뀌었는지 비교하는 서명. 캔버스·덱 참조는 빼고 본다. */
export const outlineSignature = (outline) => {
  const slides = Array.isArray(outline?.slides) ? outline.slides : [];
  return `${slides.length}:${hash(JSON.stringify([outline?.title || '', slides.map(stableSlide)]))}`;
};

export const freeDesignRef = (outline) => {
  const ref = outline?.free_design;
  return ref && typeof ref === 'object' && typeof ref.deck_id === 'string' && ref.deck_id ? ref : null;
};

/** 서버에서 새로 받은 개요(채팅 수정·버전 되돌리기)에도 만든 덱과 흐름 표시를 이어 붙인다. */
export const carryFreeDesign = (nextOutline, prevOutline) => {
  if (!nextOutline || typeof nextOutline !== 'object') return nextOutline;
  let next = nextOutline;
  if (!next.flow && prevOutline?.flow) next = { ...next, flow: prevOutline.flow };
  if (freeDesignRef(next)) return next;
  const prev = freeDesignRef(prevOutline);
  return prev ? { ...next, free_design: prev } : next;
};

const stripFreeDesign = (outline) => {
  if (!outline || typeof outline !== 'object' || !outline.free_design) return outline;
  const { free_design: _ref, ...rest } = outline;
  return rest;
};

/**
 * 버전 되돌리기에 쓸 개요.
 * 버전 문서의 outline 에 남은 free_design 은 그 버전을 만들 당시 이어 붙인 이전 덱이라 믿지 않는다.
 * 그 버전 개요로 만든 덱(versionDeck, 개요 지문이 같을 때만)이 있으면 그 덱을 바로 쓰고,
 * 없으면 지금 덱을 붙여 FreeDesignDeck 의 채팅 수정 반영(바뀐 장만/전체)이 맞춘다.
 */
export const outlineForRestore = (versionOutline, versionDeck, currentOutline) => {
  if (!versionOutline || typeof versionOutline !== 'object') return versionOutline;
  let base = stripFreeDesign(versionOutline);
  if (!base.flow && currentOutline?.flow) base = { ...base, flow: currentOutline.flow };
  const own = freeDesignRef({ free_design: versionDeck });
  if (own && own.outline_sig === outlineSignature(base)) return { ...base, free_design: own };
  const current = freeDesignRef(currentOutline);
  return current ? { ...base, free_design: current } : base;
};

/** 덱(ref)이 이 버전 개요로 만든 것인지 — 되돌리기 때 다시 만들지 않고 불러오려고 버전에 붙일지 판단 */
export const deckBelongsToVersion = (version, ref) => Boolean(
  version?.outline && ref?.deck_id && ref.outline_sig
    && outlineSignature(stripFreeDesign(version.outline)) === ref.outline_sig,
);

/**
 * 지금 덱 참조에 맞춰 버전별 덱 기록을 고칠 목록 [{ versionId, freeDesign }].
 * 덱은 채팅 수정(바뀐 장만)으로 제자리에서 바뀌므로, 같은 덱이 다른 개요로 바뀌었으면 옛 버전 기록을 지우고
 * 지금 덱이 맞는 가장 최근 버전에 붙인다. 직접 고친 글자는 개요 지문을 바꾸지 않아 기록이 그대로 남는다.
 */
export const versionDeckUpdates = (versions, currentRef) => {
  const ref = freeDesignRef({ free_design: currentRef });
  if (!ref?.outline_sig || !Array.isArray(versions)) return [];
  const updates = [];
  versions.forEach((v) => {
    const own = v?.freeDesign;
    if (v?.versionId && own?.deck_id === ref.deck_id && own.outline_sig !== ref.outline_sig) {
      updates.push({ versionId: v.versionId, freeDesign: null });
    }
  });
  const owner = [...versions].reverse().find((v) => v?.versionId && deckBelongsToVersion(v, ref));
  if (owner && owner.freeDesign?.deck_id !== ref.deck_id) {
    const { deck_id, revision, title, preview_urls, slide_count, outline_sig, slide_sigs, image_pages, created_at } = ref;
    updates.push({
      versionId: owner.versionId,
      freeDesign: {
        deck_id, revision, title, preview_urls, slide_count, outline_sig, slide_sigs,
        ...(created_at ? { created_at } : {}),
        ...(image_pages ? { image_pages } : {}),
        edited_ids: [],
      },
    });
  }
  return updates;
};

/**
 * 채팅의 「디자인만 바꾸기」를 텍스트형 덱에 할지. 텍스트형 덱이 있고 그 뒤에 만든 이미지형이 없으면 덱이다.
 * records 는 최신순 PPT 산출물 기록.
 */
export const styleUpdateTargetsDeck = (outline, records) => {
  const ref = freeDesignRef(outline);
  if (!ref) return false;
  const image = (Array.isArray(records) ? records : []).find((r) => r?.sourceMode === 'image');
  // 만든 시각이 없는 옛 참조는 덱으로 본다 — 잘못 고르면 묻지 않고 이미지형을 새로 그리게 된다.
  if (!image || !ref.created_at) return true;
  return String(ref.created_at) > String(image.createdAt || '');
};

/** 오래 걸리는 작업(이미지형 3~5분)이 시작할 때 잡아 둔 개요에, 그 사이 바뀐 같은 덱의 최신 참조를 붙인다. */
export const withLatestDeckRef = (outline, latestOutline) => {
  const own = freeDesignRef(outline);
  const latest = freeDesignRef(latestOutline);
  if (!own || !latest || own === latest || own.deck_id !== latest.deck_id) return outline;
  return { ...outline, free_design: latest };
};

const IMAGE_NOTES_MAX = 2000;

const cleanImagePages = (pages) => pages.map((page) => ({
  title: String(page?.title || '').slice(0, 200),
  notes: String(page?.notes || '').slice(0, IMAGE_NOTES_MAX),
}));

/** 이미지형 쪽마다의 제목·대본(free_design.image_pages). 순서를 바꾸거나 지워도 쪽을 따라간다. */
export const imagePagesOf = (outline) => {
  const pages = freeDesignRef(outline)?.image_pages;
  return Array.isArray(pages) ? cleanImagePages(pages) : null;
};

export const withImagePages = (outline, pages) => {
  const ref = freeDesignRef(outline);
  if (!ref || !Array.isArray(pages)) return outline;
  return { ...outline, free_design: { ...ref, image_pages: cleanImagePages(pages) } };
};

/** 덱에서 이미지형을 만들 때 — 텍스트형에서 보던 대본을 그대로 이미지형 쪽에 붙인다. */
export const imagePagesFromDeck = (deck, imageOutline) => (
  (Array.isArray(deck?.slides) ? deck.slides : []).map((slide, i) => {
    const source = imageOutline?.slides?.[i] || {};
    return { title: source.design_title || source.title || '', notes: slide?.notes || '' };
  })
);

/** 장마다의 서명 — 채팅 수정 뒤 어느 장이 바뀌었는지 찾는다. */
export const slideSignatures = (outline) => (
  (Array.isArray(outline?.slides) ? outline.slides : []).map((slide) => hash(JSON.stringify(stableSlide(slide))))
);

/**
 * 덱을 만든 개요와 지금 개요를 비교해 다시 디자인할 개요 장 번호를 돌려준다.
 *   null — 장 수가 바뀌었거나 비교할 기준이 없어 전체를 다시 만들어야 함
 *   []   — 글 내용은 같음
 */
export const changedOutlineSlides = (outline, ref) => {
  const before = Array.isArray(ref?.slide_sigs) ? ref.slide_sigs : null;
  const now = slideSignatures(outline);
  if (!before || before.length !== now.length) return null;
  return now.reduce((acc, sig, i) => (sig !== before[i] ? [...acc, i] : acc), []);
};

export const buildFreeDesignRef = (deck, outline, extra = {}) => ({
  deck_id: deck.deck_id,
  revision: deck.revision || 1,
  title: deck.title || outline?.title || '',
  preview_urls: Array.isArray(deck.preview_urls) ? deck.preview_urls : [],
  slide_count: Array.isArray(deck.slides) ? deck.slides.length : 0,
  outline_sig: outlineSignature(outline),
  slide_sigs: slideSignatures(outline),
  edited_ids: [],
  created_at: new Date().toISOString(),
  ...extra,
});

const decodeEntities = (text) => {
  if (typeof DOMParser === 'undefined') return text;
  return new DOMParser().parseFromString(`<!doctype html><body>${text}`, 'text/html').body.textContent || '';
};

/** 슬라이드 HTML 에서 화면에 보이는 글자만 줄 단위로 뽑는다 (style·svg·아이콘 제외). */
export const slidePlainText = (html) => {
  let text = String(html || '')
    .replace(/<style\b[\s\S]*?<\/style>|<svg\b[\s\S]*?<\/svg>|<script\b[\s\S]*?<\/script>/gi, ' ')
    .replace(/<br\s*\/?>|<\/(p|div|li|h[1-6]|td|th|tr|figcaption|blockquote)>/gi, '\n')
    .replace(/<[^>]+>/g, ' ');
  text = decodeEntities(text);
  const lines = text.split('\n').map((line) => line.replace(/\s+/g, ' ').trim()).filter(Boolean);
  return lines.filter((line, i) => line !== lines[i - 1]);
};

/** 슬라이드의 큰 제목(첫 h1, 없으면 h2) 글자. 없으면 ''. */
export const slideHeadingText = (html) => {
  const match = String(html || '').match(/<(h1|h2)\b[^>]*>([\s\S]*?)<\/\1>/i);
  if (!match) return '';
  return decodeEntities(match[2].replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, ' ')).replace(/\s+/g, ' ').trim();
};

/**
 * 이미지형은 텍스트형에서 본 그대로 그린다 — 덱의 순서·지운 장을 따르고,
 * 선생님이 글자를 고친 장에는 확인한 문구(design_text)와 큰 제목(design_title)을 붙인다.
 */
export const imageOutlineFromDeck = (outline, deck, editedIds = []) => {
  const source = Array.isArray(outline?.slides) ? outline.slides : [];
  const deckSlides = Array.isArray(deck?.slides) ? deck.slides : [];
  if (!outline || !deckSlides.length) return outline;
  const edited = new Set(editedIds || []);
  const slides = deckSlides.map((slide, i) => {
    const srcIndex = Number.isInteger(slide?.src) && slide.src < source.length ? slide.src : Math.min(i, source.length - 1);
    const base = { ...(source[srcIndex] || {}) };
    delete base.design_text;
    delete base.design_title;
    if (edited.has(slide.id)) {
      base.design_text = slidePlainText(slide.html).join('\n').slice(0, 800);
      const heading = slideHeadingText(slide.html).slice(0, 120);
      if (heading) base.design_title = heading;
    }
    return { ...base, slide_number: i + 1 };
  });
  const { free_design: _ref, ...rest } = outline;
  return { ...rest, slides, total_slides: slides.length };
};

/** 대화에서 선생님이 쓴 요청만 모아 디자인 참고로 보낸다(최근 것 우선, 2만 자). */
const userBodies = (messages) => {
  const bodyOf = (m) => [m.text, m.content].find((v) => typeof v === 'string' && v.trim()) || '';
  return (Array.isArray(messages) ? messages : [])
    .filter((m) => m && m.role === 'user' && bodyOf(m))
    .map((m) => bodyOf(m).trim());
};

export const requestTextFromMessages = (messages, limit = 20000) => {
  const joined = userBodies(messages).join('\n\n');
  return joined.length > limit ? joined.slice(-limit) : joined;
};

// 다시 디자인할 때 '이 PPT처럼'을 찾아볼 말 — 마지막 선생님 말만 본다(앞서 한 본보기 말이 다음 디자인 요청마다 다시 붙지 않게).
export const lastRequestText = (messages, limit = 2000) => (userBodies(messages).pop() || '').slice(0, limit);
